import React, { useState, useEffect } from 'react';
import api from '../services/api';

const ClienteleForm = () => {
  const [loading, setLoading] = useState(false);
  const [clienteleCategories, setClienteleCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeTab, setActiveTab] = useState('SIRD');
  const [newMember, setNewMember] = useState('');
  const [newClienteleType, setNewClienteleType] = useState('');
  
  // Get user role from localStorage
  const getUserRole = () => {
    try {
      return localStorage.getItem('userRole') || '';
    } catch (error) {
      console.error('Error accessing localStorage:', error);
      return '';
    }
  };
  
  // Fetch clientele categories on component mount
  useEffect(() => {
    fetchClienteleCategories();
  }, []);
  
  // Function to fetch clientele categories
  const fetchClienteleCategories = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${api.BASE_URL}/master/clientele`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch clientele');
      }
      
      const data = await response.json();
      if (data && !data.error) {
        setClienteleCategories(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching clientele:', error);
      alert('Failed to load clientele data');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle clientele type selection change
  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    if (!categoryId) {
      setSelectedCategory(null);
      return;
    }
    
    const category = clienteleCategories.find(cat => cat.id.toString() === categoryId);
    setSelectedCategory(category);
    setNewMember('');
  };
  
  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setNewMember('');
  };
  
  // Function to add a new clientele type
  const handleAddClienteleType = async () => {
    if (!newClienteleType.trim()) {
      alert('Please enter a clientele type name');
      return;
    }
    
    // Check if type already exists
    const exists = clienteleCategories.some(
      cat => cat.code.toLowerCase() === newClienteleType.trim().toLowerCase()
    );
    
    if (exists) {
      alert('This clientele type already exists');
      return;
    }
    
    setLoading(true);
    
    try {
      // Using POST API to create a new clientele type
      const newCategory = {
        code: newClienteleType.trim(),
        SIRD: [],
        RIRD: [],
        DRCP: []
      };
      
      // Changed from PUT to POST and removed the tempId
      const response = await fetch(`${api.BASE_URL}/master/clientele`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        },
        body: JSON.stringify(newCategory)
      });
      
      if (!response.ok) {
        throw new Error('Failed to create new clientele type');
      }
      
      // Refresh the clientele list to get the updated data
      await fetchClienteleCategories();
      
      setNewClienteleType('');
      alert('New clientele type added successfully');
    } catch (error) {
      console.error('Error adding clientele type:', error);
      alert('Failed to add new clientele type');
    } finally {
      setLoading(false);
    }
  };
  
  // Add new member API call
  const handleAddMember = async () => {
    if (!selectedCategory || !newMember.trim()) {
      alert('Please select a category and enter a member name');
      return;
    }
    
    // Check if member already exists
    const currentMembers = selectedCategory[activeTab] || [];
    if (currentMembers.includes(newMember)) {
      alert('This member already exists');
      return;
    }
    
    setLoading(true);
    
    try {
      const updatedMembers = [...currentMembers, newMember];
      
      // Prepare update data - only send the three arrays, no code
      const updateData = {
        SIRD: activeTab === 'SIRD' ? updatedMembers : (selectedCategory.SIRD || []),
        RIRD: activeTab === 'RIRD' ? updatedMembers : (selectedCategory.RIRD || []),
        DRCP: activeTab === 'DRCP' ? updatedMembers : (selectedCategory.DRCP || [])
      };
      
      const response = await fetch(`${api.BASE_URL}/master/clientele/${selectedCategory.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        },
        body: JSON.stringify(updateData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update clientele');
      }
      
      // Update the selected category in state
      const updatedCategory = {
        ...selectedCategory,
        [activeTab]: updatedMembers
      };
      
      setSelectedCategory(updatedCategory);
      
      // Update the clientele categories list
      setClienteleCategories(prev => 
        prev.map(cat => cat.id === selectedCategory.id ? updatedCategory : cat)
      );
      
      setNewMember('');
      alert('Member added successfully');
    } catch (error) {
      console.error('Error adding member:', error);
      alert('Failed to add member');
    } finally {
      setLoading(false);
    }
  };

  // Remove a member API call
  const handleRemoveMember = async (memberToRemove) => {
    if (!selectedCategory) return;
    
    if (!window.confirm(`Are you sure you want to remove "${memberToRemove}"?`)) {
      return;
    }
    
    const currentMembers = selectedCategory[activeTab] || [];
    const updatedMembers = currentMembers.filter(m => m !== memberToRemove);
    
    setLoading(true);
    
    try {
      // Prepare update data - only send the three arrays, no code
      const updateData = {
        SIRD: activeTab === 'SIRD' ? updatedMembers : (selectedCategory.SIRD || []),
        RIRD: activeTab === 'RIRD' ? updatedMembers : (selectedCategory.RIRD || []),
        DRCP: activeTab === 'DRCP' ? updatedMembers : (selectedCategory.DRCP || [])
      };
      
      const response = await fetch(`${api.BASE_URL}/master/clientele/${selectedCategory.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        },
        body: JSON.stringify(updateData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update clientele');
      }
      
      // Update the selected category in state
      const updatedCategory = {
        ...selectedCategory,
        [activeTab]: updatedMembers
      };
      
      setSelectedCategory(updatedCategory);
      
      // Update the clientele categories list
      setClienteleCategories(prev => 
        prev.map(cat => cat.id === selectedCategory.id ? updatedCategory : cat)
      );
      
      alert('Member removed successfully');
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Failed to remove member');
    } finally {
      setLoading(false);
    }
  };
  
  // Delete a category using PUT with status Delete
  const handleDeleteCategory = async () => {
    if (!selectedCategory) return;
    
    if (!window.confirm(`Are you sure you want to delete the category "${selectedCategory.code}"?`)) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Using PUT with status Delete instead of DELETE
      const response = await fetch(`${api.BASE_URL}/master/clientele/${selectedCategory.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        },
        body: JSON.stringify({ status: "Delete" })
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete category');
      }
      
      // Remove from clientele categories list
      setClienteleCategories(prev => prev.filter(cat => cat.id !== selectedCategory.id));
      setSelectedCategory(null);
      alert('Category deleted successfully');
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category');
    } finally {
      setLoading(false);
    }
  };
  
  // Get current members list
  const getCurrentMembers = () => {
    if (!selectedCategory) return [];
    return selectedCategory[activeTab] || [];
  };
  
  return (
    <div className="bg-gradient-to-b from-blue-50 to-white min-h-screen py-8 px-4">
      <div className="bg-white shadow-lg rounded-xl p-8 max-w-4xl mx-auto border border-gray-100">
        <h2 className="text-2xl font-bold text-blue-800 mb-6 pb-4 border-b">Clientele Management</h2>
        
        {loading && (
          <div className="text-center mb-6 p-4 bg-blue-50 rounded-lg">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-3 text-blue-700 font-medium">Loading data...</p>
          </div>
        )}
        
        {/* Clientele Type Selection */}
        <div className="mb-6">
          <label htmlFor="clienteleType" className="block text-sm font-medium text-gray-700 mb-1">
            Clientele Type <span className="text-red-500">*</span>
          </label>
          <div className="flex">
            <select
              id="clienteleType"
              value={selectedCategory ? selectedCategory.id : ''}
              onChange={handleCategoryChange}
              disabled={loading}
              className={`flex-1 rounded-md border border-gray-300 shadow-sm py-2 px-3 text-gray-700 focus:border-blue-600 focus:ring focus:ring-blue-200 focus:ring-opacity-50 transition ${loading ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            >
              <option value="">-- Select Clientele Type --</option>
              {clienteleCategories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.code}
                </option>
              ))}
            </select>
            {getUserRole() === 'department' && selectedCategory && (
              <button
                type="button"
                onClick={handleDeleteCategory}
                disabled={loading}
                className="bg-red-600 text-white rounded-md ml-2 px-4 py-2 hover:bg-red-700 focus:outline-none disabled:bg-red-300 disabled:cursor-not-allowed"
              >
                Delete
              </button>
            )}
          </div>
        </div>
        
        {/* Add New Clientele Type */}
        {getUserRole() === 'department' && (
          <div className="mb-6">
            <label htmlFor="newClienteleType" className="block text-sm font-medium text-gray-700 mb-1">
              Add New Clientele Type
            </label>
            <div className="flex">
              <input
                type="text"
                id="newClienteleType"
                value={newClienteleType}
                onChange={(e) => setNewClienteleType(e.target.value)}
                disabled={loading}
                placeholder="Enter new clientele type name"
                className={`flex-1 rounded-l-md border border-gray-300 shadow-sm py-2 px-3 text-gray-700 focus:border-blue-600 focus:ring focus:ring-blue-200 focus:ring-opacity-50 transition ${loading ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newClienteleType.trim()) {
                    e.preventDefault();
                    handleAddClienteleType();
                  }
                }}
              />
              <button
                type="button"
                onClick={handleAddClienteleType}
                disabled={!newClienteleType.trim() || loading}
                className="bg-green-600 text-white rounded-r-md px-4 py-2 hover:bg-green-700 focus:outline-none disabled:bg-green-300 disabled:cursor-not-allowed"
              >
                Add
              </button>
            </div>
          </div>
        )}
        
        {/* Institute Type Tabs */}
        {selectedCategory && (
          <div>
            <div className="border-b border-gray-200 mb-6">
              <nav className="flex -mb-px">
                <button
                  onClick={() => handleTabChange('SIRD')}
                  className={`px-4 py-2 font-medium text-sm ${
                    activeTab === 'SIRD'
                      ? 'border-b-2 border-blue-500 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  State Institute (SIRD)
                </button>
                <button
                  onClick={() => handleTabChange('RIRD')}
                  className={`px-4 py-2 font-medium text-sm ${
                    activeTab === 'RIRD'
                      ? 'border-b-2 border-blue-500 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Regional Institute (RIRD)
                </button>
                <button
                  onClick={() => handleTabChange('DRCP')}
                  className={`px-4 py-2 font-medium text-sm ${
                    activeTab === 'DRCP'
                      ? 'border-b-2 border-blue-500 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  District Resource Centers (DRCP)
                </button>
              </nav>
            </div>
            
            {/* Add new member */}
            <div className="mb-6">
              <label htmlFor="newMember" className="block text-sm font-medium text-gray-700 mb-1">
                Add New Member
              </label>
              <div className="flex">
                <input
                  type="text"
                  id="newMember"
                  value={newMember}
                  onChange={(e) => setNewMember(e.target.value)}
                  disabled={loading}
                  placeholder="Enter member name"
                  className={`flex-1 rounded-l-md border border-gray-300 shadow-sm py-2 px-3 text-gray-700 focus:border-blue-600 focus:ring focus:ring-blue-200 focus:ring-opacity-50 transition ${loading ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newMember.trim()) {
                      e.preventDefault();
                      handleAddMember();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddMember}
                  disabled={!newMember.trim() || loading}
                  className="bg-blue-600 text-white rounded-r-md px-4 py-2 hover:bg-blue-700 focus:outline-none disabled:bg-blue-300 disabled:cursor-not-allowed"
                >
                  Add
                </button>
              </div>
            </div>
            
            {/* Member List */}
            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-3">
                {activeTab} Members ({getCurrentMembers().length})
              </h3>
              
              {getCurrentMembers().length > 0 ? (
                <ul className="bg-gray-50 border border-gray-200 rounded-md divide-y divide-gray-200 max-h-96 overflow-y-auto">
                  {getCurrentMembers().map((member, index) => (
                    <li key={index} className="flex justify-between items-center px-4 py-3">
                      <span className="text-gray-700">{member}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveMember(member)}
                        disabled={loading}
                        className="text-red-500 hover:text-red-700 disabled:text-red-300 disabled:cursor-not-allowed"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-center py-6 bg-gray-50 border border-gray-200 rounded-md text-gray-500">
                  No members found for {activeTab}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClienteleForm;